import {
  formatFuelConsumption,
  formatCo2Emission,
  formatElectricConsumption,
  formatHybridConsumptionCombined,
} from "@/app/components/offers/utils/formatters";
import { CO2_LABEL_OPTIONS } from "@/app/utils/constants/backend/offers/select-options";
import { getTotalMonthlyLeasingRate } from "@/app/utils/helpers/backend/offers/get-total-monthly-leasing-rate";
import type { TEfficiencyLabel } from "@/app/components/offers/efficiency-label";
import type { OfferFormDataT } from "@/app/types/offers";

// Extract valid efficiency label values from CO2_LABEL_OPTIONS
const VALID_EFFICIENCY_LABELS: readonly TEfficiencyLabel[] =
  CO2_LABEL_OPTIONS.map(
    (option) => option.value as TEfficiencyLabel
  ) as readonly TEfficiencyLabel[];

// Helper functions
export const getConsumptionText = (offer: OfferFormDataT) => {
  const consumptionType = offer.consumption?.type;

  // Hybrid vehicles
  if (consumptionType === "hybrid" && offer.consumption?.hybrid) {
    const hybrid = offer.consumption.hybrid;
    const combinedConsumption = formatHybridConsumptionCombined(
      hybrid.stromverbrauchGewichtetKombiniert ??
        hybrid.stromverbrauchKombiniert,
      hybrid.verbrauchGewichtetKombiniert
    );
    const co2 = formatCo2Emission(hybrid.co2EmissionenGewichtetKombiniert);

    // First line: weighted combined consumption
    const line1 = `Kraftstoffverbrauch (gewichtet, kombiniert): ${combinedConsumption}, CO₂-Emissionen (gewichtet, kombiniert): ${co2}`;

    // Second line: consumption with discharged battery
    const batteryDischargedConsumption = formatFuelConsumption(
      hybrid.verbrauchKombiniertBatterieEntladen ?? hybrid.verbrauchKombiniert
    );
    const batteryDischargedCo2 =
      hybrid.co2EmissionenBatterieEntladen !== null &&
      hybrid.co2EmissionenBatterieEntladen !== undefined
        ? formatCo2Emission(hybrid.co2EmissionenBatterieEntladen)
        : "keine Angabe";
    const line2 = `Kraftstoffverbrauch bei entladener Batterie: ${batteryDischargedConsumption}, CO₂-Emissionen bei entladener Batterie: ${batteryDischargedCo2}`;

    return `${line1}\n${line2}`;
  }

  // Electric vehicles - only return text when consumption data is present
  if (consumptionType === "electric" && offer.consumption?.elektro) {
    const elektro = offer.consumption.elektro;
    const stromverbrauch = elektro.stromverbrauchKombiniert;
    if (stromverbrauch == null || stromverbrauch <= 0) {
      return "";
    }
    const consumption = formatElectricConsumption(stromverbrauch);
    return `Stromverbrauch (kombiniert): ${consumption}, CO₂-Emissionen: 0 g/km`;
  }

  // Gasoline/Diesel/Truck vehicles
  if (
    (consumptionType === "gasoline" ||
      consumptionType === "diesel" ||
      consumptionType === "truck") &&
    offer.consumption?.kraftstoff?.verbrauchKombiniert
  ) {
    const consumption = formatFuelConsumption(
      offer.consumption.kraftstoff.verbrauchKombiniert
    );
    const co2 = formatCo2Emission(offer.consumption.kraftstoff.co2Emissionen);
    return `Kraftstoffverbrauch (kombiniert): ${consumption}${co2 !== "-" ? `, CO₂-Emissionen: ${co2}` : ""}`;
  }

  return "";
};

export const getEfficiencyLabel = (
  offer: OfferFormDataT
): "A" | "B" | "C" | "D" | "E" | "F" | "G" | undefined => {
  const consumptionType = offer.consumption?.type;

  // Hybrid vehicles - return first label (for backward compatibility)
  if (
    consumptionType === "hybrid" &&
    offer.consumption?.hybrid?.co2LabelGewichtetKombiniert
  ) {
    return offer.consumption.hybrid
      .co2LabelGewichtetKombiniert as TEfficiencyLabel;
  }

  // Electric vehicles
  if (
    consumptionType === "electric" &&
    offer.consumption?.elektro?.energielabel
  ) {
    return offer.consumption.elektro.energielabel as TEfficiencyLabel;
  }

  // Gasoline/Diesel/Truck vehicles
  if (offer.consumption?.kraftstoff?.co2Label) {
    return offer.consumption.kraftstoff.co2Label as TEfficiencyLabel;
  }

  return undefined;
};

/**
 * Get both efficiency labels for hybrid vehicles
 * Returns an object with co2LabelGewichtetKombiniert and co2LabelBatterieEntladen
 */
export const getHybridEfficiencyLabels = (
  offer: OfferFormDataT
): {
  co2LabelGewichtetKombiniert?: TEfficiencyLabel;
  co2LabelBatterieEntladen?: TEfficiencyLabel;
} => {
  const consumptionType = offer.consumption?.type;

  if (consumptionType === "hybrid" && offer.consumption?.hybrid) {
    const hybrid = offer.consumption.hybrid;
    const result: {
      co2LabelGewichtetKombiniert?: TEfficiencyLabel;
      co2LabelBatterieEntladen?: TEfficiencyLabel;
    } = {};

    if (
      hybrid.co2LabelGewichtetKombiniert &&
      VALID_EFFICIENCY_LABELS.includes(
        hybrid.co2LabelGewichtetKombiniert as TEfficiencyLabel
      )
    ) {
      result.co2LabelGewichtetKombiniert =
        hybrid.co2LabelGewichtetKombiniert as TEfficiencyLabel;
    }

    if (
      hybrid.co2LabelBatterieEntladen &&
      VALID_EFFICIENCY_LABELS.includes(
        hybrid.co2LabelBatterieEntladen as TEfficiencyLabel
      )
    ) {
      result.co2LabelBatterieEntladen =
        hybrid.co2LabelBatterieEntladen as TEfficiencyLabel;
    }

    return result;
  }

  return {};
};

export type PriceTextResultT = {
  price: string;
  pricingLabel: string;
  vatLabel?: string;
};

const formatPriceAmount = (value: number) =>
  new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

/**
 * Get price text with optional VAT label
 * Returns an object with price and optional vatLabel for formatting
 */
export const getPriceText = (
  offer: OfferFormDataT
): PriceTextResultT | string => {
  const pricingType = offer.pricing?.type;
  const isBusiness =
    offer.offer_types?.some((type) => type.key === "business") ?? false;
  const vatLabel = isBusiness ? "zzgl. MwSt." : "inkl. MwSt.";

  if (pricingType === "leasing") {
    const totalMonthlyRate = getTotalMonthlyLeasingRate(offer);
    if (totalMonthlyRate) {
      return {
        price: `ab mtl. ${formatPriceAmount(totalMonthlyRate)} €`,
        pricingLabel: "Leasing:",
        vatLabel,
      };
    }
  }

  if (pricingType === "barkauf") {
    // For business: prioritize netto price with "zzgl. MwSt."
    // Only show netto price if available
    if (isBusiness && offer.pricing?.barkauf?.anschaffungspreisNetto) {
      const nettoPrice = offer.pricing.barkauf.anschaffungspreisNetto;
      return {
        price: `${formatPriceAmount(nettoPrice)} €`,
        pricingLabel: "Barkauf:",
        vatLabel,
      };
    }

    // For all other cases: use brutto price (NEVER show "zzgl. MwSt." with brutto)
    if (offer.pricing?.barkauf?.anschaffungspreisBrutto) {
      const bruttoPrice = offer.pricing.barkauf.anschaffungspreisBrutto;
      return {
        price: `${formatPriceAmount(bruttoPrice)} €`,
        pricingLabel: "Barkauf:",
        vatLabel,
      };
    }
  }

  if (pricingType === "finanzierung") {
    if (offer.pricing?.finanzierung?.mtlRate) {
      return {
        price: `ab mtl. ${formatPriceAmount(offer.pricing.finanzierung.mtlRate)} €`,
        pricingLabel: "Finanzierung:",
        vatLabel,
      };
    }

    if (isBusiness && offer.pricing?.finanzierung?.anschaffungspreisNetto) {
      return {
        price: `${formatPriceAmount(
          offer.pricing.finanzierung.anschaffungspreisNetto
        )} €`,
        pricingLabel: "Finanzierung:",
        vatLabel,
      };
    }

    if (offer.pricing?.finanzierung?.anschaffungspreisBrutto) {
      return {
        price: `${formatPriceAmount(
          offer.pricing.finanzierung.anschaffungspreisBrutto
        )} €`,
        pricingLabel: "Finanzierung:",
        vatLabel,
      };
    }
  }

  return "";
};
